import React from 'react';
import { Link } from 'react-router-dom';
import { imageUrl } from '../../lib/opinly';

const SAFE_HREF = /^(https?:|mailto:|tel:|\/|#)/i;
const VIDEO_HOSTS = /^https:\/\/(www\.)?(youtube\.com|youtube-nocookie\.com|youtu\.be|player\.vimeo\.com)\//;
const NAVY = '#1a3a5c';
const h = React.createElement;

export const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9äöüß]+/g, '-').replace(/^-|-$/g, '');
const plainText = (node) => (node.content || []).map((c) => c.text || '').join('');

// ---------- inline marks ----------
const MARKS = {
  bold: (el) => h('strong', null, el),
  italic: (el) => h('em', null, el),
  underline: (el) => h('u', null, el),
  strike: (el) => h('s', null, el),
  subscript: (el) => h('sub', null, el),
  superscript: (el) => h('sup', null, el),
  code: (el) => h('code', { className: 'px-1.5 py-0.5 rounded bg-slate-100 text-[0.9em]', style: { color: NAVY } }, el),
  highlight: (el) => h('mark', { style: { background: '#d7ecf4' } }, el),
  textStyle: (el, a) => h('span', { style: { color: a.color || undefined } }, el),
  link: (el, a) => {
    const href = a.href || '';
    if (!SAFE_HREF.test(href)) return el;
    const props = { className: 'underline underline-offset-2 font-medium', style: { color: '#2d7d9a' } };
    if (href.startsWith('/') && !href.startsWith('//')) return h(Link, { to: href, ...props }, el);
    return h('a', { href, target: a.target || '_blank', rel: 'noopener noreferrer', ...props }, el);
  },
};

const wrapMark = (el, mark) => (MARKS[mark.type] ? MARKS[mark.type](el, mark.attrs || {}) : el);
const Marked = ({ node }) => (node.marks || []).reduce(wrapMark, node.text ?? '');

// createElement (not JSX) on purpose: the dev-only visual-edits babel plugin cannot handle recursive JSX components
const Children = ({ nodes }) => (nodes || []).map((n, i) => h(Node, { key: i, node: n }));
const kids = (node) => h(Children, { nodes: node.content });

// ---------- block nodes ----------
const Heading = ({ node, a, align }) => {
  const level = Math.min(Math.max(a.level || 2, 2), 6);
  const Tag = `h${level}`;
  return <Tag id={a.id || slugify(plainText(node))} style={{ color: NAVY, ...align }} className="scroll-mt-28">{kids(node)}</Tag>;
};

const Image = ({ a }) => {
  const src = imageUrl(a.fileKey) || (a.src && SAFE_HREF.test(a.src) ? a.src : null);
  if (!src) return null;
  return (
    <figure className="my-8">
      <img src={src} alt={a.alt || ''} loading="lazy" className="w-full rounded-2xl shadow-md" />
    </figure>
  );
};

const Video = ({ a }) => {
  if (!VIDEO_HOSTS.test(a.src || '')) return null;
  return <div className="my-8 aspect-video rounded-2xl overflow-hidden shadow-md"><iframe src={a.src} title="Video" className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>;
};

const BLOCKS = {
  doc: ({ node }) => kids(node),
  text: ({ node }) => <Marked node={node} />,
  paragraph: ({ node, align }) => (node.content ? <p style={align}>{kids(node)}</p> : <p className="h-4" />),
  heading: Heading,
  bulletList: ({ node }) => <ul>{kids(node)}</ul>,
  orderedList: ({ node, a }) => <ol start={a.start || 1}>{kids(node)}</ol>,
  listItem: ({ node }) => <li>{kids(node)}</li>,
  taskList: ({ node }) => <ul className="list-none pl-0">{kids(node)}</ul>,
  taskItem: ({ node, a }) => <li className="flex gap-2 items-start"><input type="checkbox" checked={!!a.checked} readOnly className="mt-1.5" /><div>{kids(node)}</div></li>,
  blockquote: ({ node }) => <blockquote className="border-l-4 pl-5 italic text-slate-700" style={{ borderColor: '#3d94b8' }}>{kids(node)}</blockquote>,
  codeBlock: ({ node }) => <pre className="rounded-xl p-4 overflow-x-auto text-sm text-white" style={{ background: NAVY }}><code>{plainText(node)}</code></pre>,
  horizontalRule: () => <hr className="my-10 border-slate-200" />,
  hardBreak: () => <br />,
  image: Image,
  youtube: Video,
  video: Video,
  table: ({ node }) => <div className="overflow-x-auto my-6"><table className="w-full text-sm border-collapse"><tbody>{kids(node)}</tbody></table></div>,
  tableRow: ({ node }) => <tr className="border-b border-slate-200">{kids(node)}</tr>,
  tableHeader: ({ node, a }) => <th colSpan={a.colspan} rowSpan={a.rowspan} className="text-left font-bold p-3" style={{ background: '#e6f2f7', color: NAVY }}>{kids(node)}</th>,
  tableCell: ({ node, a }) => <td colSpan={a.colspan} rowSpan={a.rowspan} className="p-3 align-top">{kids(node)}</td>,
};

function Node({ node }) {
  if (!node) return null;
  const a = node.attrs || {};
  const Block = BLOCKS[node.type];
  if (Block) return <Block node={node} a={a} align={a.textAlign ? { textAlign: a.textAlign } : undefined} />;
  return node.content ? kids(node) : null;
}

export default function ContentRenderer({ content }) {
  return (
    <div data-testid="post-content" className="prose prose-slate prose-lg max-w-none prose-headings:font-extrabold prose-a:no-underline prose-img:rounded-2xl prose-li:marker:text-[#3d94b8]">
      {h(Node, { node: content })}
    </div>
  );
}

export const extractHeadings = (content) => {
  const out = [];
  const walk = (n) => {
    if (n?.type === 'heading' && (n.attrs?.level || 2) <= 3) {
      const text = plainText(n);
      out.push({ text, id: n.attrs?.id || slugify(text), level: n.attrs?.level || 2 });
    }
    (n?.content || []).forEach(walk);
  };
  walk(content);
  return out;
};
